// components/ProductComparison.js
import React, { useState, useMemo, useEffect } from 'react';
import {
  Button,
  Card,
  Typography,
  Tabs,
  Tab,
  ToggleButtonGroup,
  ToggleButton,
  Box,
  CircularProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArgonTypography from 'components/ArgonTypography';

import ArgonBox from 'components/ArgonBox';
import { compareProducts } from 'services/product';

import ProductDetailsReview from 'layouts/commit/reviewComponents/ProductDetailsReview';
import ComponentsReview from 'layouts/commit/reviewComponents/ComponentsReview';
import RevenueAttributes from 'layouts/commit/reviewComponents/RevenueAttributes';
import ProductRulesReview from 'layouts/commit/reviewComponents/ProductRulesReview';
import PricingModelReview from 'layouts/commit/reviewComponents/PricingModelReview';
import PricebooksReview from 'layouts/commit/reviewComponents/PricebooksReview';
import CPQAttributesReview from 'layouts/commit/reviewComponents/CPQAttributesReview';

const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  '&.MuiToggleButton-root': {
    border: 'none',
    background: 'transparent',
    '&.Mui-selected': {
      backgroundColor: theme.palette.action.selected,
      '&:hover': {
        backgroundColor: theme.palette.action.selected,
      },
    },
  },
}));

function ProductComparison({ org, products, isRevenue, onBack }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [comparisonResult, setComparisonResult] = useState(null);

  const product1 = products[0];
  const product2 = products[1];

  const tabData = useMemo(
    () => [
      {
        label: 'Product Details',
        key: 'productDetails',
      },
      {
        label: 'Components',
        key: 'components',
      },
      ...(isRevenue
        ? [
            {
              label: 'Attributes',
              key: 'revenueAttributes',
            },
          ]
        : [
            {
              label: 'Product Rules',
              key: 'productRules',
            },
            {
              label: 'Attributes',
              key: 'cpqAttributes',
            },
          ]),
      {
        label: 'Pricing Model',
        key: 'pricingModel',
      },
      {
        label: 'Pricebooks',
        key: 'pricebooks',
      },
    ],
    [isRevenue]
  );

  useEffect(() => {
    const performComparison = async () => {
      // Guard against invalid props
      if (!product1 || !product2) {
        setError('Two products are required for comparison.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        // NEW PATTERN: Call the service without userId.
        // The service returns the data object directly or throws an error.
        const response = await compareProducts(product1, product2, false, org.org_id);

        // The response.diff contains the comparison object.
        // We wrap it in an array because the review components expect an array of diffs.
        setComparisonResult([response.diff]);
      } catch (err) {
        console.error('Error comparing products:', err);
        setError(err.message || 'Failed to compare products.');
      } finally {
        setLoading(false);
      }
    };

    performComparison();
  }, [product1, product2, org.org_id]); // Dependencies are the two products being compared

  const [activeReviewStage, setActiveReviewStage] = useState('productDetails');
  const [showSameFields, setShowSameFields] = useState(true);

  const handleTabChange = (event, newValue) => {
    setActiveReviewStage(newValue);
  };

  // Handle change for the toggle buttons - FIXED
  const handleShowSameChange = (event, newValue) => {
    if (newValue !== null) {
      // Only update state if newValue is NOT null
      setShowSameFields(newValue);
    }
  };

  const renderReviewContent = () => {
    // Pass the comparisonResult to the review components
    const productData = comparisonResult;

    switch (activeReviewStage) {
      case 'productDetails':
        return <ProductDetailsReview selectedOrg={org} productData={productData} showSameFields={showSameFields} />;
      case 'components':
        return <ComponentsReview selectedOrg={org} productData={productData} showSameFields={showSameFields} />;
      case 'revenueAttributes':
        return <RevenueAttributes selectedOrg={org} productData={productData} showSameFields={showSameFields} />;
      case 'productRules':
        return <ProductRulesReview selectedOrg={org} productData={productData} showSameFields={showSameFields} />;
      case 'cpqAttributes':
        return <CPQAttributesReview selectedOrg={org} productData={productData} showSameFields={showSameFields} />;
      case 'pricingModel':
        return <PricingModelReview selectedOrg={org} productData={productData} showSameFields={showSameFields} />;
      case 'pricebooks':
        return <PricebooksReview selectedOrg={org} productData={productData} showSameFields={showSameFields} />;
      default:
        return null;
    }
  };

  if (!products || products.length !== 2) {
    return (
      <ArgonBox p={3}>
        <Typography variant="h6">Select exactly two products to compare.</Typography>
        <Button onClick={onBack} variant="contained" color="primary">
          Back
        </Button>
      </ArgonBox>
    );
  }

  return (
    <ArgonBox py={3}>
      <Card>
        <ArgonBox p={3}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Tabs
              value={activeReviewStage}
              onChange={handleTabChange}
              aria-label="review-tabs"
              indicatorColor="primary"
              textColor="primary"
              centered
              sx={{ width: '-webkit-fill-available', marginRight: '1rem' }}
            >
              {tabData.map((tab, index) => (
                <Tab key={index} label={tab.label} value={tab.key} />
              ))}
            </Tabs>
            <ToggleButtonGroup
              value={showSameFields}
              exclusive
              onChange={handleShowSameChange}
              aria-label="Show Same Fields"
              size="small"
            >
              <StyledToggleButton value={false} aria-label="Hide Same Fields">
                <VisibilityOffIcon />
              </StyledToggleButton>
              <StyledToggleButton value={true} aria-label="Show Same Fields">
                <VisibilityIcon />
              </StyledToggleButton>
            </ToggleButtonGroup>
          </Box>
          <ArgonBox mt={3}>
            {/* Loading and Error Handling */}
            <Card>
              {loading ? (
                <ArgonBox
                  p={3}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                >
                  <CircularProgress color="info" />
                </ArgonBox>
              ) : error ? (
                <ArgonBox
                  p={3}
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  minHeight="200px"
                >
                  <ArgonTypography variant="body1" color="error">
                    {error}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                renderReviewContent()
              )}
            </Card>
          </ArgonBox>
        </ArgonBox>
      </Card>
    </ArgonBox>
  );
}

export default ProductComparison;
